import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, Printer } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatPrice } from "@/lib/constants";

export default function OfferPrint() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [failedImages, setFailedImages] = useState([]);

  useEffect(() => {
    api.get(`/offers/${id}/print-data`)
      .then(({ data: result }) => setData(result))
      .catch((err) => setError(formatApiError(err)));
  }, [id]);

  if (error && !data) return <div className="p-8 text-red-700">{error}</div>;
  if (!data) return <div className="p-8 text-slate-500">Yükleniyor…</div>;

  const { offer, unit, project, company, agent } = data;
  const details = [
    ["Proje", project?.name], ["Blok", unit?.block_name], ["Kat", unit?.floor_name],
    ["Bölüm", unit?.unit_no], ["Bölüm tipi", unit?.unit_type],
    ["Oda", unit?.room_type], ["Plan tipi", unit?.layout_type],
    ["Net alan", unit?.net_area != null ? `${unit.net_area} m²` : ""],
    ["Brüt alan", unit?.gross_area != null ? `${unit.gross_area} m²` : ""],
    ["Cephe", unit?.aspect], ["Manzara", unit?.view],
  ].filter(([, value]) => value !== "" && value != null);
  const image = [unit?.plan_url, ...(unit?.image_urls || [])].filter(Boolean).find((url) => !failedImages.includes(url));
  const floorPlanUrl = unit?.floor_plan_url && !failedImages.includes(unit.floor_plan_url) ? unit.floor_plan_url : "";
  const logoUrl = company?.logo_url && !failedImages.includes(company.logo_url) ? company.logo_url : "";
  const coverUrl = project?.cover_image_url && !failedImages.includes(project.cover_image_url) ? project.cover_image_url : "";
  const markImageFailed = (url) => setFailedImages((current) => current.includes(url) ? current : [...current, url]);
  const primary = company?.primary_color || "#0F172A";
  const accent = company?.secondary_color || "#F59E0B";

  async function downloadPdf() {
    setDownloading(true);
    setError("");
    try {
      const response = await api.get(`/offers/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `teklif-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-100 py-6 px-4 text-slate-900" data-testid="offer-print-page">
      <style>{`@page { size: A4; margin: 14mm; } @media print {
        body { background: white !important; }
        .print-actions { display: none !important; }
        .print-sheet { box-shadow: none !important; border: 0 !important; padding: 0 !important; width: 100% !important; }
        .print-image { max-height: 62mm !important; }
      }`}</style>
      <div className="print-actions max-w-[210mm] mx-auto flex items-center justify-between mb-4 gap-3 flex-wrap">
        <Link to={`/units/${offer.unit_id || ""}`} onClick={(e) => { if (!offer.unit_id) e.preventDefault(); }}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-slate-200 rounded">
          <ArrowLeft className="w-4 h-4" /> Bölüme dön
        </Link>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => window.print()}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-white border border-slate-200 rounded">
            <Printer className="w-4 h-4" /> Yazdır
          </button>
          <button type="button" onClick={downloadPdf} disabled={downloading}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-slate-900 rounded disabled:opacity-60">
            <Download className="w-4 h-4" /> {downloading ? "Hazırlanıyor..." : "PDF İndir"}
          </button>
        </div>
      </div>
      {error && <div className="print-actions max-w-[210mm] mx-auto mb-3 text-sm text-red-700">{error}</div>}
      <main className="print-sheet max-w-[210mm] mx-auto bg-white border border-slate-200 border-t-8 shadow-sm p-8 md:p-10" style={{ borderTopColor: primary }}>
        <header className="flex items-start justify-between gap-6 border-b border-slate-200 pb-6">
          <div className="flex items-center gap-3 min-w-0">
            {logoUrl ? <img src={logoUrl} alt="" className="h-12 w-12 object-contain" onError={() => markImageFailed(logoUrl)} /> : (
              <span className="flex h-12 w-12 items-center justify-center text-white font-bold text-lg rounded" style={{ background: primary }}>
                {(company?.name || "G").charAt(0)}
              </span>
            )}
            <div>
              <div className="text-lg font-bold">{company?.name || "Gayrimenkul"}</div>
              <div className="text-xs text-slate-500">{company?.phone} {company?.email && `• ${company.email}`}</div>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs uppercase tracking-widest text-slate-500">Fiyat Teklifi</div>
            <div className="text-sm font-semibold mt-1">{(offer.created_at || "").slice(0, 10)}</div>
          </div>
        </header>

        <section className="grid grid-cols-2 gap-5 py-6 border-b border-slate-200 text-sm">
          <div><div className="text-xs uppercase text-slate-500">Sayın</div><div className="font-semibold mt-1">{offer.customer_name || "Değerli Müşterimiz"}</div></div>
          <div><div className="text-xs uppercase text-slate-500">Satış Temsilcisi</div><div className="font-semibold mt-1">{agent?.name || "—"}</div><div className="text-xs text-slate-500">{agent?.phone}</div></div>
        </section>

        <section className="py-6">
          <div className="flex items-center gap-5 bg-slate-50 p-4">
            {coverUrl && <img src={coverUrl} alt="" className="h-24 w-32 object-cover rounded" onError={() => markImageFailed(coverUrl)} />}
            <div className="min-w-0">
              <h1 className="text-2xl font-bold">{project?.name || "Proje"}</h1>
              <div className="text-sm font-semibold mt-1">{[unit?.block_name, unit?.floor_name, unit?.unit_no].filter(Boolean).join(" / ")}</div>
              {project?.location && <p className="text-sm text-slate-500 mt-1">{project.location}</p>}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4 mt-6">
            {details.map(([label, value]) => (
              <div key={label} className="border-b border-slate-100 pb-2">
                <div className="text-xs text-slate-500">{label}</div>
                <div className="text-sm font-semibold mt-1">{value}</div>
              </div>
            ))}
          </div>
        </section>

        {(floorPlanUrl || image) && <div className={`border-t border-slate-200 py-5 grid gap-4 ${floorPlanUrl && image ? "md:grid-cols-2" : "grid-cols-1"}`}>
          {floorPlanUrl && <div>
            <div className="text-xs uppercase text-slate-500 mb-3">Kat Planı</div>
            <img src={floorPlanUrl} alt="Kat planı" className="print-image w-full max-h-64 object-contain bg-slate-50"
                 onError={() => markImageFailed(floorPlanUrl)} />
          </div>}
          {image && <div>
            <div className="text-xs uppercase text-slate-500 mb-3">{image === unit?.plan_url ? "Daire Planı" : "Örnek Daire"}</div>
            <img src={image} alt="Daire planı veya örnek daire" className="print-image w-full max-h-64 object-contain bg-slate-50"
                 onError={() => markImageFailed(image)} />
          </div>}
        </div>}

        <section className="border-t border-slate-200 pt-5 flex items-end justify-between gap-6">
          <div className="text-sm text-slate-600">
            {offer.valid_until && <div>Geçerlilik tarihi: <span className="font-semibold text-slate-900">{offer.valid_until.slice(0, 10)}</span></div>}
            <div className="mt-1">İletişim: {agent?.phone || company?.phone || "—"}</div>
          </div>
          {offer.include_price && <div className="text-right" style={{ color: accent }}>
            <div className="text-xs uppercase text-slate-500">Teklif Fiyatı</div>
            <div className="text-2xl font-bold mt-1">{formatPrice(offer.offered_price, offer.currency)}</div>
          </div>}
        </section>
      </main>
    </div>
  );
}
